/**
 * System Design Canvas Types
 * Architecture diagramming tool for system design
 */

// Component types
export type ComponentType =
  | 'service'
  | 'database'
  | 'cache'
  | 'queue'
  | 'cdn'
  | 'load-balancer'
  | 'gateway'
  | 'storage'
  | 'function'
  | 'container'
  | 'client'
  | 'external';

// Connection types
export type ConnectionType = 'http' | 'grpc' | 'websocket' | 'pubsub' | 'tcp' | 'async';

// Data flow direction
export type DataFlow = 'request' | 'response' | 'async' | 'bidirectional';

// Cloud providers
export type CloudProvider = 'aws' | 'gcp' | 'azure' | 'generic';

// Position
export interface Position {
  x: number;
  y: number;
}

// Size
export interface Size {
  width: number;
  height: number;
}

// Scaling configuration
export interface ScalingConfig {
  replicas?: number;
  minReplicas?: number;
  maxReplicas?: number;
  strategy?: 'horizontal' | 'vertical' | 'auto';
  sharded?: boolean;
  shardCount?: number;
}

// Cost estimation
export interface CostEstimate {
  provider: CloudProvider;
  service?: string;
  monthlyCost: number;
  notes?: string;
}

// System component
export interface SystemComponent {
  id: string;
  type: ComponentType;
  name: string;
  position: Position;
  size?: Size;
  technology?: string;
  description?: string;
  scaling?: ScalingConfig;
  cost?: CostEstimate;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

// Connection between components
export interface SystemConnection {
  id: string;
  sourceId: string;
  targetId: string;
  connectionType: ConnectionType;
  dataFlow: DataFlow;
  label?: string;
  protocol?: string;
  port?: number;
  async?: boolean;
  metadata?: Record<string, unknown>;
}

// Annotation on the canvas
export interface SystemAnnotation {
  id: string;
  type: 'text' | 'note' | 'region';
  content: string;
  position: Position;
  size?: Size;
  color?: string;
}

// Full diagram
export interface SystemDesignDiagram {
  id: string;
  name: string;
  description?: string;
  components: SystemComponent[];
  connections: SystemConnection[];
  annotations: SystemAnnotation[];
  settings: DiagramSettings;
  totalMonthlyCost?: number;
  createdAt: string;
  updatedAt: string;
}

// Diagram settings
export interface DiagramSettings {
  gridEnabled: boolean;
  gridSize: number;
  snapToGrid: boolean;
  showCosts: boolean;
  showScaling: boolean;
  canvasSize: Size;
  zoom: number;
  pan: Position;
}

// Feature access for premium gating
export interface SystemDesignFeatures {
  canEdit: boolean;
  canSave: boolean;
  maxDiagrams: number;
  maxComponents: number;
  canExportSvg: boolean;
  canExportMarkdown: boolean;
  canEstimateCosts: boolean;
  canUseTechSuggestions: boolean;
  canUseTemplates: boolean;
}

// Component type configuration
export const COMPONENT_TYPE_CONFIG: Record<ComponentType, {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  defaultSize: Size;
  description: string;
  technologies: string[];
}> = {
  service: {
    label: 'Service',
    icon: '⚙️',
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    defaultSize: { width: 120, height: 80 },
    description: 'Microservice or application',
    technologies: ['Node.js', 'Python', 'Go', 'Java', 'Rust', '.NET', 'Ruby'],
  },
  database: {
    label: 'Database',
    icon: '🗄️',
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    defaultSize: { width: 100, height: 80 },
    description: 'Data storage',
    technologies: ['PostgreSQL', 'MySQL', 'MongoDB', 'DynamoDB', 'Cassandra', 'Redis', 'Elasticsearch'],
  },
  cache: {
    label: 'Cache',
    icon: '⚡',
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'In-memory cache',
    technologies: ['Redis', 'Memcached', 'Hazelcast', 'ElastiCache'],
  },
  queue: {
    label: 'Queue',
    icon: '📬',
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'Message queue',
    technologies: ['Kafka', 'RabbitMQ', 'SQS', 'Pub/Sub', 'NATS', 'Redis Streams'],
  },
  cdn: {
    label: 'CDN',
    icon: '🌐',
    color: '#06B6D4',
    bgColor: 'rgba(6, 182, 212, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'Content delivery network',
    technologies: ['CloudFront', 'Cloudflare', 'Fastly', 'Akamai'],
  },
  'load-balancer': {
    label: 'Load Balancer',
    icon: '⚖️',
    color: '#EC4899',
    bgColor: 'rgba(236, 72, 153, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'Traffic distribution',
    technologies: ['ALB', 'NLB', 'HAProxy', 'nginx', 'Envoy', 'Traefik'],
  },
  gateway: {
    label: 'API Gateway',
    icon: '🚪',
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'API management',
    technologies: ['Kong', 'API Gateway', 'Apigee', 'Tyk', 'Ambassador'],
  },
  storage: {
    label: 'Storage',
    icon: '💾',
    color: '#6366F1',
    bgColor: 'rgba(99, 102, 241, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'Object/file storage',
    technologies: ['S3', 'GCS', 'Azure Blob', 'MinIO'],
  },
  function: {
    label: 'Function',
    icon: 'λ',
    color: '#F97316',
    bgColor: 'rgba(249, 115, 22, 0.1)',
    defaultSize: { width: 90, height: 70 },
    description: 'Serverless function',
    technologies: ['Lambda', 'Cloud Functions', 'Azure Functions', 'Cloudflare Workers'],
  },
  container: {
    label: 'Container',
    icon: '📦',
    color: '#0EA5E9',
    bgColor: 'rgba(14, 165, 233, 0.1)',
    defaultSize: { width: 110, height: 80 },
    description: 'Container/pod',
    technologies: ['Docker', 'Kubernetes', 'ECS', 'Cloud Run'],
  },
  client: {
    label: 'Client',
    icon: '👤',
    color: '#84CC16',
    bgColor: 'rgba(132, 204, 22, 0.1)',
    defaultSize: { width: 90, height: 70 },
    description: 'Client application',
    technologies: ['Web', 'Mobile', 'Desktop', 'CLI'],
  },
  external: {
    label: 'External',
    icon: '🔗',
    color: '#6B7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
    defaultSize: { width: 100, height: 70 },
    description: 'External service',
    technologies: ['Third-party API', 'SaaS', 'Partner'],
  },
};

// Connection type configuration
export const CONNECTION_TYPE_CONFIG: Record<ConnectionType, {
  label: string;
  color: string;
  strokeStyle: 'solid' | 'dashed' | 'dotted';
  description: string;
}> = {
  http: {
    label: 'HTTP/REST',
    color: '#3B82F6',
    strokeStyle: 'solid',
    description: 'HTTP/REST API calls',
  },
  grpc: {
    label: 'gRPC',
    color: '#10B981',
    strokeStyle: 'solid',
    description: 'gRPC protocol',
  },
  websocket: {
    label: 'WebSocket',
    color: '#8B5CF6',
    strokeStyle: 'solid',
    description: 'WebSocket connection',
  },
  pubsub: {
    label: 'Pub/Sub',
    color: '#F59E0B',
    strokeStyle: 'dashed',
    description: 'Publish/Subscribe messaging',
  },
  tcp: {
    label: 'TCP',
    color: '#6B7280',
    strokeStyle: 'solid',
    description: 'Raw TCP connection',
  },
  async: {
    label: 'Async',
    color: '#EC4899',
    strokeStyle: 'dashed',
    description: 'Asynchronous messaging',
  },
};

// Data flow configuration
export const DATA_FLOW_CONFIG: Record<DataFlow, {
  label: string;
  arrowStart: boolean;
  arrowEnd: boolean;
}> = {
  request: {
    label: 'Request',
    arrowStart: false,
    arrowEnd: true,
  },
  response: {
    label: 'Response',
    arrowStart: true,
    arrowEnd: false,
  },
  async: {
    label: 'Async',
    arrowStart: false,
    arrowEnd: true,
  },
  bidirectional: {
    label: 'Bidirectional',
    arrowStart: true,
    arrowEnd: true,
  },
};

// Default diagram settings
export const DEFAULT_DIAGRAM_SETTINGS: DiagramSettings = {
  gridEnabled: true,
  gridSize: 20,
  snapToGrid: true,
  showCosts: false,
  showScaling: true,
  canvasSize: { width: 2000, height: 1500 },
  zoom: 1,
  pan: { x: 0, y: 0 },
};

// Cloud provider pricing (simplified estimates)
export const CLOUD_PRICING: Record<CloudProvider, Record<ComponentType, { baseCost: number; unit: string }>> = {
  aws: {
    service: { baseCost: 50, unit: 'per instance/month' },
    database: { baseCost: 100, unit: 'per instance/month' },
    cache: { baseCost: 30, unit: 'per node/month' },
    queue: { baseCost: 25, unit: 'per million messages' },
    cdn: { baseCost: 20, unit: 'per TB' },
    'load-balancer': { baseCost: 25, unit: 'per month' },
    gateway: { baseCost: 35, unit: 'per million requests' },
    storage: { baseCost: 23, unit: 'per TB/month' },
    function: { baseCost: 0.20, unit: 'per million invocations' },
    container: { baseCost: 40, unit: 'per vCPU/month' },
    client: { baseCost: 0, unit: '' },
    external: { baseCost: 0, unit: '' },
  },
  gcp: {
    service: { baseCost: 45, unit: 'per instance/month' },
    database: { baseCost: 90, unit: 'per instance/month' },
    cache: { baseCost: 28, unit: 'per node/month' },
    queue: { baseCost: 20, unit: 'per million messages' },
    cdn: { baseCost: 18, unit: 'per TB' },
    'load-balancer': { baseCost: 22, unit: 'per month' },
    gateway: { baseCost: 30, unit: 'per million requests' },
    storage: { baseCost: 20, unit: 'per TB/month' },
    function: { baseCost: 0.40, unit: 'per million invocations' },
    container: { baseCost: 35, unit: 'per vCPU/month' },
    client: { baseCost: 0, unit: '' },
    external: { baseCost: 0, unit: '' },
  },
  azure: {
    service: { baseCost: 48, unit: 'per instance/month' },
    database: { baseCost: 95, unit: 'per instance/month' },
    cache: { baseCost: 32, unit: 'per node/month' },
    queue: { baseCost: 22, unit: 'per million messages' },
    cdn: { baseCost: 19, unit: 'per TB' },
    'load-balancer': { baseCost: 24, unit: 'per month' },
    gateway: { baseCost: 33, unit: 'per million requests' },
    storage: { baseCost: 21, unit: 'per TB/month' },
    function: { baseCost: 0.25, unit: 'per million invocations' },
    container: { baseCost: 38, unit: 'per vCPU/month' },
    client: { baseCost: 0, unit: '' },
    external: { baseCost: 0, unit: '' },
  },
  generic: {
    service: { baseCost: 50, unit: 'per instance/month' },
    database: { baseCost: 100, unit: 'per instance/month' },
    cache: { baseCost: 30, unit: 'per node/month' },
    queue: { baseCost: 25, unit: 'per million messages' },
    cdn: { baseCost: 20, unit: 'per TB' },
    'load-balancer': { baseCost: 25, unit: 'per month' },
    gateway: { baseCost: 35, unit: 'per million requests' },
    storage: { baseCost: 23, unit: 'per TB/month' },
    function: { baseCost: 0.20, unit: 'per million invocations' },
    container: { baseCost: 40, unit: 'per vCPU/month' },
    client: { baseCost: 0, unit: '' },
    external: { baseCost: 0, unit: '' },
  },
};
